/* eslint-disable id-blacklist */
/* eslint-disable no-bitwise */
export const CURRENCY_DIVIDER = 100;

// @todo core/types/Currency
export enum Currency {
  'USD' = 1 << 2, // US dollars
  'EUR' = 1 << 3, // Euro Currency
  'GBP' = 1 << 4, // British Pounds
  'CAD' = 1 << 5, // Canadian Dollars
  'INR' = 1 << 6 // Indian Rupees
}

export const prefixMap = {
  USD: '$',
  GBP: '£',
  EUR: '€',
  INR: '₹',
  CAD: 'C$',
  RUB: '₽',
  SEK: 'kr',
  CHF: 'CHF'
};

export interface FormatOptions {
  // shorten large number display e.g. 1000 = 1K, 1000000 = 1M etc
  shorten?: boolean;
  shortenRM?: boolean;
  // add thousand separators e.g. 1000 = 1,000
  separators?: boolean;
  scalePM?: number;
  isPlayMoney?: number;
  divider?: number;
  locale?: string;
  shortPrizePool?: boolean;
}

export const addThousandsSeparator = (amount: number, separator = ','): string =>
  String(amount).replace(/\B(?=(\d{3})+(?!\d))/g, separator);

const getAmountNoCents = (amount: number, divider?: number): number =>
  Math.floor(amount / (divider || CURRENCY_DIVIDER));

// sometimes PM flag comes back false, but the currency is '' indicating game or amount is PM  -- Checking in to test POKER-5706
const isPlayMoneyCurrency = (currencyStr: string, isPlayMoney?: number): boolean => {
  if (isPlayMoney !== undefined && currencyStr !== undefined && currencyStr.length > 0) {
    return Boolean(isPlayMoney);
  }
  return !currencyStr || currencyStr === 'PLAY';
};

const roundToPrecision = (number: number, precision: number) => {
  const prec = 10 ** precision;
  return Math.floor(number * prec) / prec;
};

const isEnLocale = (locale?: string) => locale === 'en' || locale === 'en-initial';

const getShortenedRealMoneyAmountSuffix = (amount: number, locale?: string) => {
  if (amount) {
    if (amount >= 1000000000000) {
      return {
        amount: roundToPrecision(amount / 1000000000000, 2),
        suffix: isEnLocale(locale) ? ' TRILLION' : 'T'
      };
    }
    if (amount >= 1000000000) {
      return {
        amount: roundToPrecision(amount / 1000000000, 2),
        suffix: isEnLocale(locale) ? ' BILLION' : 'B'
      };
    }
    if (amount >= 1000000) {
      return {
        amount: roundToPrecision(amount / 1000000, 2),
        suffix: isEnLocale(locale) ? ' MILLION' : 'M'
      };
    }
  }
  return {
    amount,
    suffix: ''
  };
};

export const getShortenedAmountSuffix = (amount: number) => {
  if (amount) {
    if (amount >= 1000000000000) {
      return {
        amount: roundToPrecision(amount / 1000000000000, 2),
        suffix: 'T'
      };
    }
    if (amount >= 1000000000) {
      return {
        amount: roundToPrecision(amount / 1000000000, 2),
        suffix: 'B'
      };
    }
    if (amount >= 1000000) {
      return {
        amount: roundToPrecision(amount / 1000000, 2),
        suffix: 'M'
      };
    }
    if (amount >= 1000) {
      return {
        amount: roundToPrecision(amount / 1000, 2),
        suffix: 'K'
      };
    }
  }
  return {
    amount,
    suffix: ''
  };
};

const formatRealMoneyAmount = (
  amount: number,
  currencyStr: string,
  separators: boolean,
  divider?: number
) => {
  const amountNoCents = getAmountNoCents(amount, divider);
  const cents = Math.floor(Number(amount) % Number(divider || CURRENCY_DIVIDER));
  // @ts-ignore TODO: fix types
  const prefix = prefixMap[currencyStr];
  // ensure any cent values are represented as 2 characters
  const suffix = cents > 0 ? `.${`0${String(cents)}`.slice(-2)}` : '';
  let dollarStr = String(amountNoCents);
  if (separators) {
    dollarStr = addThousandsSeparator(amountNoCents);
  }
  // additional check for valid amounts here
  return amount >= 0 ? `${prefix}${dollarStr}${suffix}` : '';
};

const formatPlayMoneyAmount = (
  amount: number,
  scalePM?: number,
  divider?: number,
  shorten?: boolean,
  separator?: boolean
) => {
  const scaledAmount = scalePM ? amount * scalePM : amount;
  let amountNoCents = getAmountNoCents(scaledAmount, divider);
  let suffix = '';
  if (shorten) {
    ({ amount: amountNoCents, suffix } = getShortenedAmountSuffix(amountNoCents));
  }
  return `${separator ? addThousandsSeparator(amountNoCents) : amountNoCents}${suffix}`;
};

const formatShortenRealMoneyAmount = (
  amount: number,
  currencyStr: string,
  divider?: number,
  locale?: string
) => {
  const amountNoCents = getAmountNoCents(amount, divider);
  // @ts-ignore TODO: fix types
  const prefix = prefixMap[currencyStr];

  const { amount: shortenedAmount, suffix } = getShortenedRealMoneyAmountSuffix(
    amountNoCents,
    locale
  );

  return `${prefix}${shortenedAmount}${suffix}`;
};

export const formatShortPrizePoolAmount = (
  amount: number,
  currencyStr: string,
  divider?: number
) => {
  const amountNoCents = getAmountNoCents(amount, divider);
  // @ts-ignore TODO: fix types
  const prefix = prefixMap[currencyStr];

  const { amount: shortenedAmount, suffix } = getShortenedAmountSuffix(amountNoCents);

  return `${prefix}${shortenedAmount}${suffix}`;
};

export default function formatCurrency(
  amount: number,
  currency: string | number,
  options: FormatOptions = {}
) {
  // sanitise currency value to a string with default to USD
  const currencyStr = typeof currency === 'number' ? Currency[currency] || 'USD' : currency;
  const {
    scalePM,
    isPlayMoney,
    separators,
    divider,
    shorten = true,
    shortenRM = false,
    locale,
    shortPrizePool
  } = options;

  if (isPlayMoneyCurrency(currencyStr, isPlayMoney)) {
    return formatPlayMoneyAmount(amount, scalePM, divider, shorten, separators);
  }

  if (shortPrizePool) {
    return formatShortPrizePoolAmount(amount, currencyStr);
  }

  if (shortenRM && amount >= 100000000) {
    return formatShortenRealMoneyAmount(amount, currencyStr, divider, locale);
  }

  return formatRealMoneyAmount(amount, currencyStr, !!separators, divider);
}
